import {Box} from '@viptech/react-components'
import React, {useEffect, useMemo, useState} from 'react'
import {Group, Image, Layer, Line, Stage} from 'react-konva'
import {DetectionTooltip} from 'src/common/components/detection-tooltip/DetectionTooltip'
import {DetectionTooltipProps} from 'src/common/generics/props/DetectionTooltipProps'
import {ImageDetectionsProps} from 'src/common/generics/props/ImageDetectionsProps'
import {Dim, Pos} from 'src/common/types/generics/ImageDetections'
import {boxScale, boxToPoints, computeScalar, detectionToBox, dim2pos, pos2dim, posDefault, posScale} from 'src/common/utils/Internal'
import useImage from 'src/hooks/useImage'
import {useCameraConfigContext} from 'src/pages/camera-config/context/CameraConfigContext'

export function CamerasKonvaImageDetections({
  src,
  detections = [],
  hideDetections = false,
  scale = 1,
  children,
  StageProps,
  konvaImageRef,
  pointsToDrawWithSvg,
}: React.PropsWithChildren<ImageDetectionsProps>) {
  const {_dimImage, setDimImage} = useCameraConfigContext()
  const {image, width: imageWidth, height: imageHeight} = useImage(src)

  const proportionalResolution = (originalWidth: number, originalHeight: number) => {
    const FIXED_HEIGHT = 345
    const proportionalWidth = (originalWidth * FIXED_HEIGHT) / originalHeight

    return {width: isNaN(proportionalWidth) ? FIXED_HEIGHT : proportionalWidth, height: FIXED_HEIGHT}
  }

  const {width, height} = proportionalResolution(imageWidth, imageHeight)

  useEffect(() => {
    if (imageWidth > 0 && imageHeight > 0) {
      setDimImage({width: imageWidth, height: imageHeight})
    }
  }, [imageWidth, imageHeight, setDimImage])

  const [tooltipProps, setTooltipProps] = useState<DetectionTooltipProps>({
    x: 0,
    y: 0,
    text: '',
    visible: false,
  })
  const internalScale = computeScalar(scale, _dimImage, {width, height})
  const posImage: Pos = dim2pos(_dimImage)
  const dimScaled: Dim = pos2dim(posDefault(posScale(posImage, internalScale), posImage))

  function updateTooltipProps(props: Partial<DetectionTooltipProps>) {
    setTooltipProps((lastProps) => ({...lastProps, ...props}))
  }

  const naturalImageDim: Dim = {width: imageWidth, height: imageHeight}

  const lines = useMemo(() => {
    return detections.map((it, index) => {
      const box = boxScale(detectionToBox(it, naturalImageDim), internalScale)
      const colorOnDetection = it.data && it.data.additionalData && it.data.additionalData.boundBoxColor ? it.data.additionalData.boundBoxColor : 'red'

      return (
        <React.Fragment key={`konva-line-${index}`}>
          <Line
            key={`detection-line-${index}`}
            points={boxToPoints(box)}
            strokeWidth={2 * internalScale}
            stroke={colorOnDetection}
            shadowColor='red'
            closed
            data={it.data}
          />
        </React.Fragment>
      )
    })
  }, [internalScale, detections, naturalImageDim.width, naturalImageDim.height])

  if (konvaImageRef) {
    const currentRef = konvaImageRef.current
    const dimChanged = currentRef?.dim.width !== dimScaled.width || currentRef?.dim.height !== dimScaled.height
    if (currentRef === null || currentRef.scale !== internalScale || dimChanged) {
      konvaImageRef.current = {dim: dimScaled, scale: internalScale}
    }
  }

  return (
    <Box position='relative' width='100%'>
      <Stage {...StageProps} {...dimScaled}>
        <Layer
          onMouseOver={(event) => {
            const pointer = event?.target?.getStage()?.getPointerPosition()
            const attrs = event?.target?.getAttrs()
            if (pointer && attrs.data) {
              setTooltipProps({
                text: `${(100 * attrs.data.accuracy).toFixed(1)}% ${attrs.data.class}`,
                x: pointer.x,
                y: pointer.y,
                visible: true,
              })
            } else {
              updateTooltipProps({visible: false})
            }
          }}
          onMouseOut={() => updateTooltipProps({visible: false})}>
          {image && <Image image={image} {...dimScaled} />}
          <Group name='detection-areas'>{!hideDetections && lines}</Group>
        </Layer>
        <DetectionTooltip {...tooltipProps} />
        {dimScaled.height !== 0 && children}
      </Stage>

      {pointsToDrawWithSvg && pointsToDrawWithSvg.length > 0 && (
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: dimScaled.width,
            height: dimScaled.height,
            pointerEvents: 'none',
            zIndex: 90000,
          }}
          xmlns='http://www.w3.org/2000/svg'>
          <line
            x1={pointsToDrawWithSvg[0].x * dimScaled.width}
            y1={pointsToDrawWithSvg[0].y * dimScaled.height}
            x2={pointsToDrawWithSvg[1].x * dimScaled.width}
            y2={pointsToDrawWithSvg[1].y * dimScaled.height}
            stroke='red'
            strokeWidth='2'
          />
        </svg>
      )}
    </Box>
  )
}
